import type {
  UpstreamChannelRecord,
  UpstreamModelCapabilityRecord,
  UpstreamModelCostRecord,
  UpstreamSkuRoutingIdentitySummary,
} from '@nadoutong/contracts';

/**
 * Pure derivations behind the upstream model SKU table (epic #1327 PR-5).
 *
 * Kept out of the component because each one encodes a counting rule that
 * is easy to get subtly wrong and worth asserting directly.
 */

export interface ModelSkuRow {
  /** Exact physical capability the row acts on — never a canonical sibling. */
  readonly capability: UpstreamModelCapabilityRecord;
  /** The model name an operator recognises: the upstream's own wire name. */
  readonly label: string;
}

export interface ModelSkuGroup {
  readonly channelId: string;
  readonly channelSlug: string;
  readonly enabled: boolean;
  /**
   * True when the namespace only exists in capability evidence — the
   * channel row itself is gone, so discovery cannot run against it.
   */
  readonly stale: boolean;
  readonly rows: readonly ModelSkuRow[];
}

function rowLabel(capability: UpstreamModelCapabilityRecord): string {
  return capability.source_model || capability.upstream_model_name || capability.model;
}

/**
 * One group per upstream namespace, including live channels with zero
 * discovered models (an operator needs the discover button there most)
 * and stale channels that only survive in capability evidence.
 */
export function groupCapabilitiesByNamespace(
  capabilities: readonly UpstreamModelCapabilityRecord[],
  channels: readonly UpstreamChannelRecord[],
): ModelSkuGroup[] {
  const groups = new Map<string, {
    channelId: string;
    channelSlug: string;
    enabled: boolean;
    stale: boolean;
    rows: ModelSkuRow[];
  }>();
  for (const channel of channels) {
    groups.set(channel.channel_id, {
      channelId: channel.channel_id,
      channelSlug: channel.channel_slug,
      enabled: channel.enabled,
      stale: false,
      rows: [],
    });
  }
  for (const capability of capabilities) {
    let group = groups.get(capability.channel_id);
    if (group === undefined) {
      group = {
        channelId: capability.channel_id,
        channelSlug: capability.channel_slug,
        enabled: false,
        stale: true,
        rows: [],
      };
      groups.set(capability.channel_id, group);
    }
    group.rows.push({ capability, label: rowLabel(capability) });
  }
  return [...groups.values()]
    .map((group) => ({
      ...group,
      rows: [...group.rows].sort((a, b) =>
        a.label.localeCompare(b.label) || a.capability.id.localeCompare(b.capability.id)
      ),
    }))
    .sort((a, b) => a.channelSlug.localeCompare(b.channelSlug) || a.channelId.localeCompare(b.channelId));
}

function costKey(channelId: string, model: string): string {
  return `${channelId}\u0000${model}`;
}

/**
 * `(channel, physical model) → multiplier`. The vendor is carried on the
 * record but is not part of the key: the gateway's own unique index is
 * `(upstream_id, upstream_channel_id, model)`.
 */
export function indexModelCosts(
  costs: readonly UpstreamModelCostRecord[],
): ReadonlyMap<string, UpstreamModelCostRecord> {
  const index = new Map<string, UpstreamModelCostRecord>();
  for (const cost of costs) index.set(costKey(cost.upstream_channel_id, cost.model), cost);
  return index;
}

export function modelCostFor(
  index: ReadonlyMap<string, UpstreamModelCostRecord>,
  capability: UpstreamModelCapabilityRecord,
): UpstreamModelCostRecord | undefined {
  return index.get(costKey(capability.channel_id, capability.model));
}

/**
 * `capability_id → distinct platform SKU count`.
 *
 * DISTINCT is the load-bearing word: the routing summary emits one row
 * per (SKU × scope-bearing identity), so a SKU bound in more than one
 * scope appears repeatedly and a plain row count would overstate how
 * many products a model actually serves.
 *
 * Identities whose `capability_id` is null are legacy unpinned targets;
 * they are matched on canonical vendor/model instead, which is the same
 * fallback the routing summary itself uses.
 */
export function countServedSkusByCapability(
  identities: readonly UpstreamSkuRoutingIdentitySummary[],
  capabilities: readonly UpstreamModelCapabilityRecord[],
): ReadonlyMap<string, number> {
  const skusByCapability = new Map<string, Set<string>>();
  for (const capability of capabilities) skusByCapability.set(capability.id, new Set());
  for (const identity of identities) {
    if (identity.capability_id !== null) {
      skusByCapability.get(identity.capability_id)?.add(identity.sku_id);
      continue;
    }
    for (const capability of capabilities) {
      if (
        capability.vendor === identity.vendor &&
        capability.model === identity.model &&
        capability.account_id === identity.account_id &&
        capability.key_bucket_id === identity.key_bucket_id &&
        capability.channel_id === identity.upstream_channel_id
      ) {
        skusByCapability.get(capability.id)?.add(identity.sku_id);
      }
    }
  }
  return new Map([...skusByCapability].map(([capabilityId, skus]) => [capabilityId, skus.size]));
}
